import React, { useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Grid,
  Stack,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import { Breadcrumb, SimpleCard } from "app/components";
import { useDispatch, useSelector } from "react-redux";
import { Formik } from "formik";
import * as Yup from "yup";

import AttachFileIcon from "@mui/icons-material/AttachFile";
import { TextField1 } from "app/assets";
import { useAuth } from "app/hooks/useAuth";
import { changeUserPassword, updateCurrentUser } from "app/redux/actions";
import { toast } from "react-toastify";
import Loading from "app/components/MatxLoading";
import { LIVE_URL } from "config";

// Validation Schema
const profileValidationSchema = Yup.object().shape({
  name: Yup.string().required("Name is required"),
  email: Yup.string()
    .email("Invalid email format")
    .required("Email is required"),

  // CNIC validation
  cnic: Yup.string()
    .required("CNIC is required")
    .matches(
      /^(?:\d{13}|\d{5}-\d{7}-\d{1})$/,
      "CNIC format must be: 0000000000000 OR 00000-0000000-0"
    ),

  // Phone validation
  phone: Yup.string()
    .required("Mobile number is required")
    .matches(
      /^(?:\d{11}|\d{4}-\d{7}|\+92\d{10}|\+92\s\d{10})$/,
      "Phone format must be: 00000000000 OR 0000-0000000 OR +920000000000 OR +92 0000000000"
    ),

  avatar: Yup.string().nullable(),
  cnic_front: Yup.string().nullable(),
  cnic_back: Yup.string().nullable(),
  // account_no: Yup.string().required("Account Number Is Required")
});

const passwordValidationSchema = Yup.object().shape({
  oldPassword: Yup.string().required("Old password is required"),
  newPassword: Yup.string()
    .required("Password is required")
    .min(6, "Password must be at least 6 characters")
    .matches(/[A-Z]/, "Password must contain at least one uppercase letter")
    .matches(/[a-z]/, "Password must contain at least one lowercase letter")
    .matches(/\d/, "Password must contain at least one number")
    .matches(/[@$!%*?&]/, "Password must contain at least one special character"),

  confirmPassword: Yup.string()
    .oneOf([Yup.ref("newPassword"), null], "Passwords must match")
    .required("Confirm password is required"),
});

const Profile = () => {
  const auth = useAuth();
  console.log(auth, 'check auth role')
  const [dialogOpen, setDialogOpen] = useState(false);

  const user = useSelector((state) => state.user);

  const dispatch = useDispatch();

  const handleSubmit = async (values) => {
    console.log('values', values);
    const formData = new FormData();

    // append normal fields
    for (const key in values) {
      if (values[key] instanceof File) {
        formData.append(key, values[key]);
      } else {
        formData.append(key, values[key] ?? "");
      }
    }


    // dispatch API call
    dispatch(
      updateCurrentUser(formData, auth.token, moveToNext, moveToFailure)
    );
  };


  const moveToNext = () => {
    toast.success("Profile updated successfully!");
  };
  const moveToFailure = () => {
    toast.error("Failed to update profile.");
  };

  const handleFileUpload = (e, setFieldValue, fieldName) => {
    const file_ = e.target.files[0];
    if (file_) {
      setFieldValue(fieldName, file_); // ✅ store File object directly
    }
  };
  const handleRemoveFile = (fieldName, setFieldValue) => {
    setFieldValue(fieldName, null);
  };
  const handleDialogOpen = () => {
    setDialogOpen(true);
  };
  return (
    <SimpleCard>
      <Box sx={{ p: 3 }}>
        <Box className="breadcrumb">
          <Breadcrumb routeSegments={[{ name: "Profile" }]} />
        </Box>
        <Formik
          initialValues={{
            id: auth.id,
            name: auth.name,
            email: auth.email,
            cnic: auth.cnic,
            phone: auth.phone,
            avatar: auth.avatar,
            cnic_front: auth.cnic_front,
            cnic_back: auth.cnic_back,
            account_no: auth.account_no || ""
          }}
          enableReinitialize
          validationSchema={profileValidationSchema}
          onSubmit={handleSubmit}
        >
          {(formik_props) => {
            const {
              values,
              handleChange,
              handleSubmit,
              setFieldValue,
              errors,
              touched,
            } = formik_props;
            return (
              <Box component="form" onSubmit={handleSubmit}>
                <Stack spacing={3}>
                  <Grid container spacing={2}>
                    {/* <Grid
                      item
                      xs={12}
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        marginBottom: "20px",
                      }}
                    >
                      <label htmlFor="avatar-upload">
                        <Avatar
                          sx={{ width: 100, height: 100, cursor: "pointer" }}
                          src={
                            values.avatar instanceof File
                              ? URL.createObjectURL(values.avatar) // preview if new file
                              : `${LIVE_URL}public/${values.avatar}` // show old one from server
                          }
                        />
                        <input
                          id="avatar-upload"
                          type="file"
                          accept="image/*"
                          hidden
                          onChange={(event) => handleFileUpload(event, setFieldValue, "avatar")}
                        />
                      </label>
                    </Grid> */}
                    <Grid
                      item
                      xs={12}
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        marginBottom: "20px",
                        position: "relative",
                      }}
                    >
                      <label htmlFor="avatar-upload">
                        <Avatar
                          sx={{ width: 100, height: 100, cursor: "pointer" }}
                          src={
                            values.avatar instanceof File
                              ? URL.createObjectURL(values.avatar) // preview new file
                              : values.avatar
                                ? `${LIVE_URL}public/${values.avatar}` // show from server
                                : "" // empty if removed
                          }
                        />
                        <input
                          id="avatar-upload"
                          type="file"
                          accept="image/*"
                          hidden
                          onChange={(event) =>
                            handleFileUpload(event, setFieldValue, "avatar")
                          }
                        />
                      </label>

                      {values.avatar && (
                        <Button
                          size="small"
                          variant="contained"
                          color="error"
                          onClick={() => setFieldValue("avatar", "")}
                          sx={{
                            position: "absolute",
                            top: 0,
                            right: "calc(50% - 50px)", // adjust relative to avatar size
                            minWidth: "24px",
                            width: "24px",
                            height: "24px",
                            borderRadius: "50%",
                            p: 0,
                          }}
                        >
                          ✕
                        </Button>
                      )}
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sx={{ display: "flex", justifyContent: "end" }}
                    >
                      <Button variant="outlined" onClick={handleDialogOpen}>
                        Change Password
                      </Button>
                    </Grid>

                    {/* Profile Fields */}
                    <Grid item xs={12} sm={6}>
                      <TextField1
                        name="name"
                        label="Name"
                        value={values.name}
                        onChange={handleChange}
                        error={Boolean(touched.name && errors.name)}
                        helperText={touched.name && errors.name}
                        required
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField1
                        name="email"
                        label="Email"
                        value={values.email}
                        onChange={handleChange}
                        error={Boolean(touched.email && errors.email)}
                        helperText={touched.email && errors.email}
                        required
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField1
                        name="cnic"
                        label="CNIC"
                        value={values.cnic}
                        onChange={handleChange}
                        error={Boolean(touched.cnic && errors.cnic)}
                        helperText={touched.cnic && errors.cnic}
                        required
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField1
                        name="phone"
                        label="Phone"
                        value={values.phone}
                        onChange={handleChange}
                        error={Boolean(touched.phone && errors.phone)}
                        helperText={touched.phone && errors.phone}
                        required
                      />
                    </Grid>

                    <Grid item lg={6} md={6} sm={12} xs={12}>
                      <Button
                        variant="outlined"
                        component="label"
                        startIcon={<AttachFileIcon />}
                      >
                        Upload CNIC Front
                        <input
                          type="file"
                          hidden
                          onChange={(event) => handleFileUpload(event, setFieldValue, "cnic_front")}
                        />
                      </Button>

                      {/* Show selected file with remove */}
                      {values.cnic_front && (
                        <Box mt={1} display="flex" alignItems="center" gap={2}>
                          {values.cnic_front instanceof File ? (
                            <img
                              src={URL.createObjectURL(values.cnic_front)}
                              alt="CNIC Front"
                              style={{ width: 100, height: "auto", borderRadius: 4 }}
                            />
                          ) : (
                            <img
                              src={`${LIVE_URL}public${values.cnic_front}`}
                              alt="CNIC Front"
                              style={{ width: 100, height: "auto", borderRadius: 4 }}
                            />
                          )}
                          <Button
                            size="small"
                            color="error"
                            onClick={() => handleRemoveFile("cnic_front", setFieldValue)}
                          >
                            Remove
                          </Button>
                        </Box>
                      )}
                    </Grid>

                    <Grid item lg={6} md={6} sm={12} xs={12}>
                      <Button
                        variant="outlined"
                        component="label"
                        startIcon={<AttachFileIcon />}
                      >
                        Upload CNIC Back
                        <input
                          type="file"
                          hidden
                          onChange={(event) => handleFileUpload(event, setFieldValue, "cnic_back")}
                        />
                      </Button>

                      {/* Show selected file with remove */}
                      {values.cnic_back && (
                        <Box mt={1} display="flex" alignItems="center" gap={2}>
                          {values.cnic_back instanceof File ? (
                            <img
                              src={URL.createObjectURL(values.cnic_back)}
                              alt="CNIC Back"
                              style={{ width: 100, height: "auto", borderRadius: 4 }}
                            />
                          ) : (
                            <img
                              src={`${LIVE_URL}public${values.cnic_back}`}
                              alt="CNIC Back"
                              style={{ width: 100, height: "auto", borderRadius: 4 }}
                            />
                          )}
                          <Button
                            size="small"
                            color="error"
                            onClick={() => handleRemoveFile("cnic_back", setFieldValue)}
                          >
                            Remove
                          </Button>
                        </Box>
                      )}
                    </Grid>
                    <Grid item xs={12}>
                      {user.updateLoading === false ? (
                        <Button variant="contained" type="submit">
                          Update
                        </Button>
                      ) : (
                        <Loading />
                      )}
                    </Grid>
                  </Grid>
                </Stack>
              </Box>
            );
          }}
        </Formik>
      </Box>
      <ChangePassword
        dialogOpen={dialogOpen}
        setDialogOpen={setDialogOpen} />

    </SimpleCard>
  );
};

export default Profile;


const ChangePassword = ({ dialogOpen, setDialogOpen }) => {
  const auth = useAuth();
  const user = useSelector((state) => state.user);
  const dispatch = useDispatch();
  const [passwordValues, setPasswordValues] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const handleDialogClose = () => {
    setDialogOpen(false);
    setPasswordValues({
      oldPassword: "",
      newPassword: "",
      confirmPassword: "",
    });
  };
  const handlePasswordChange = async (values) => {
    dispatch(
      changeUserPassword(
        values,
        auth,
        changeToPasswordSuccess,
        changeToPasswordFailure
      )
    );
  };
  const changeToPasswordSuccess = () => {
    toast.success("Password updated successfully!");
    handleDialogClose();
  };
  const changeToPasswordFailure = (message) => {
    toast.error(message);
  };
  return (
    <Dialog open={dialogOpen} onClose={handleDialogClose}>
      <DialogTitle>Change Password</DialogTitle>
      <DialogContent>
        <Formik
          initialValues={passwordValues}
          enableReinitialize
          validationSchema={passwordValidationSchema}
          onSubmit={handlePasswordChange}
        >
          {(formik_props) => {
            const { values, handleChange, handleSubmit, errors, touched } =
              formik_props;
            return (
              <Box component="form" onSubmit={handleSubmit}>
                <TextField
                  margin="dense"
                  label="Old Password"
                  type="password"
                  fullWidth
                  variant="outlined"
                  name="oldPassword"
                  value={values.oldPassword}
                  onChange={handleChange}
                  error={Boolean(touched.oldPassword && errors.oldPassword)}
                  helperText={touched.oldPassword && errors.oldPassword}
                />
                <TextField
                  margin="dense"
                  label="New Password"
                  type="password"
                  fullWidth
                  variant="outlined"
                  name="newPassword"
                  value={values.newPassword}
                  onChange={handleChange}
                  error={Boolean(touched.newPassword && errors.newPassword)}
                  helperText={touched.newPassword && errors.newPassword}
                />
                <TextField
                  margin="dense"
                  label="Confirm New Password"
                  type="password"
                  fullWidth
                  variant="outlined"
                  name="confirmPassword"
                  value={values.confirmPassword}
                  onChange={handleChange}
                  error={Boolean(
                    touched.confirmPassword && errors.confirmPassword
                  )}
                  helperText={
                    touched.confirmPassword && errors.confirmPassword
                  }
                />
                <DialogActions>
                  {user.updatePasswordLoading === false ? (
                    <>
                      <Button variant="outlined" onClick={handleDialogClose}>
                        Cancel
                      </Button>
                      <Button variant="contained" type="submit">
                        Save
                      </Button>
                    </>
                  ) : (
                    <Loading />
                  )}
                </DialogActions>
              </Box>
            );
          }}
        </Formik>
      </DialogContent>
    </Dialog>
  )
}